import { useMemo } from 'react';
import { useTranslate } from '@prenuvo/halo';

import { useUser } from '@/core/user';
import { PII_ROLE } from '@/core/user/role.constant';
import { useStudyGuard } from '@/shared/guard';

import { extractSkuFromSkuId, getSkuPackage } from '../../util';
import { getPatientDisplayName, getPatientDob, getServiceInfoNameList } from './header.util';

export function usePatientPermissionsAndDetails() {
  const translate = useTranslate();
  const {
    isPoetryStudy,
    patient,
    radiologistScan: { machineID, poetryMetadata, skuID },
    studyId,
  } = useStudyGuard();
  const { getRoles } = useUser();

  const isAusScanner = machineID?.startsWith('aus-');
  const hasPIIAccess = getRoles([PII_ROLE]) && isAusScanner;
  const skuPackage = isPoetryStudy
    ? `${getServiceInfoNameList(poetryMetadata?.servicesInfo ?? []).join(', ')}`
    : getSkuPackage(extractSkuFromSkuId(skuID));
  const patientDisplayDob = getPatientDob(patient, hasPIIAccess, translate);
  const patientPrenuvoID = patient.prenuvoID.replace('auth0|', '');
  const patientDisplayName = getPatientDisplayName(
    patient,
    hasPIIAccess,
    translate('FCT.REPORTING.HEADER.PATIENT.UNKNOWN_NAME'),
  );
  const truncatedPatientID = `${patientPrenuvoID.slice(0, 6)}...`;
  const identifier = poetryMetadata?.accessionNumber || `${studyId.slice(0, 8)}`;

  const value = useMemo(
    () => ({
      hasPIIAccess,
      identifier,
      isAusScanner,
      patientDisplayDob,
      patientDisplayName,
      patientPrenuvoID,
      skuPackage,
      truncatedPatientID,
    }),
    [
      isAusScanner,
      identifier,
      hasPIIAccess,
      skuPackage,
      patientDisplayDob,
      patientPrenuvoID,
      patientDisplayName,
      truncatedPatientID,
    ],
  );

  return value;
}
